import React from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  ExternalLink
} from 'lucide-react';

const Info = ({ LayoutComponent = Layout }) => {
  const apiEndpoints = [
    {
      feature: 'Auth Token',
      endpoint: 'POST /v1/auth/token',
      description: 'PayItNow API authentication',
      dataSource: 'PIN API',
      status: 'active',
      notes: 'Required for all banking calls. Token expires ~5 min.'
    },
    {
      feature: 'Balance',
      endpoint: 'GET /v1/balance',
      description: 'Account balance (available, credits, debits, reserved)',
      dataSource: 'PIN API when creds valid, else Dummy',
      status: 'active',
      notes: 'Used for Dashboard + Primary AUD account balance'
    },
    {
      feature: 'Transactions',
      endpoint: 'GET /v1/transactions',
      description: 'Transaction history (all types, deposit, withdrawal)',
      dataSource: 'PIN API when creds valid, else Dummy',
      status: 'active',
      notes: 'Supports type=deposit, pagination, date filters'
    },
    {
      feature: 'Create Payment',
      endpoint: 'POST /v1/payments',
      description: 'Create outgoing payment',
      dataSource: 'PIN API in Live (KYC approved), Dummy in Sandbox',
      status: 'active',
      notes: 'Live: real payment. Sandbox: simulated response.'
    },
    {
      feature: 'Payment Status',
      endpoint: 'GET /v1/payments/{id}',
      description: 'Get payment status',
      dataSource: 'PIN API when creds valid, else Dummy',
      status: 'active',
      notes: 'Returns status from PayItNow or local DB fallback'
    },
    {
      feature: 'Accounts (PIN)',
      endpoint: 'GET /v1/accounts',
      description: 'Real sandbox/live account numbers from PayItNow',
      dataSource: 'PIN API if endpoint exists, else Not used',
      status: 'optional',
      notes: 'App uses internal accounts; PIN accounts used when available'
    },
  ];

  const appFeatures = [
    {
      feature: 'User Registration',
      api: 'Internal MongoDB',
      dataSource: 'Real Data',
      description: 'User account creation with email verification'
    },
    {
      feature: 'Email Verification',
      api: 'Internal MongoDB',
      dataSource: 'Demo Data',
      description: 'Code shown on screen; production would send email'
    },
    {
      feature: '2FA Authentication',
      api: 'PyOTP',
      dataSource: 'Real Data',
      description: 'TOTP compatible with Google/MS Authenticator'
    },
    {
      feature: 'Dashboard Balance & Stats',
      api: 'PIN /v1/balance',
      dataSource: 'PIN API or Dummy',
      description: 'Uses PayItNow when credentials valid; else dummy demo data'
    },
    {
      feature: 'Dashboard Recent Transactions',
      api: 'PIN /v1/transactions',
      dataSource: 'PIN API or Dummy',
      description: 'Includes deposits & withdrawals; dummy when PIN unavailable'
    },
    {
      feature: 'Accounts – Primary AUD Balance',
      api: 'PIN /v1/balance',
      dataSource: 'PIN API or Dummy',
      description: 'Primary AUD balance from PayItNow when available'
    },
    {
      feature: 'Accounts – Recent Deposits',
      api: 'PIN /v1/transactions?type=deposit',
      dataSource: 'PIN API or Dummy',
      description: 'Deposit-only transactions; dummy when PIN unavailable'
    },
    {
      feature: 'Accounts – Multi-Currency (USD, EUR, USDC, USDT)',
      api: 'Internal MongoDB',
      dataSource: 'Dummy',
      description: 'Simulated balances; not from PayItNow'
    },
    {
      feature: 'Accounts – Account Numbers',
      api: 'Internal / PIN',
      dataSource: 'Dummy or PIN',
      description: 'Dummy in sandbox; real from PIN when /v1/accounts available'
    },
    {
      feature: 'Transactions Page',
      api: 'PIN /v1/transactions',
      dataSource: 'PIN API or Dummy',
      description: 'Full history with filters; dummy when PIN unavailable'
    },
    {
      feature: 'Payments',
      api: 'PIN /v1/payments',
      dataSource: 'PIN API or Dummy',
      description: 'Live: real. Sandbox: simulated. KYC required in Live.'
    },
    {
      feature: 'Analytics & Charts',
      api: 'Internal Calculation',
      dataSource: 'Dummy',
      description: 'Simulated trends; not from PayItNow'
    },
    {
      feature: 'Export Transactions',
      api: 'PIN /v1/transactions',
      dataSource: 'PIN API or Dummy',
      description: 'Export uses same source as Transactions page'
    },
    {
      feature: 'User Management',
      api: 'Internal MongoDB',
      dataSource: 'Real Data',
      description: 'RBAC: Admin, Manager, Viewer'
    },
    {
      feature: 'Audit Logs',
      api: 'Internal MongoDB',
      dataSource: 'Real Data',
      description: 'All user actions logged'
    },
  ];

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[#E6F7F3] text-[#1C472B]">
            <CheckCircle className="w-3.5 h-3.5" />
            Active
          </span>
        );
      case 'sandbox':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700">
            <AlertTriangle className="w-3.5 h-3.5" />
            Sandbox Only
          </span>
        );
      case 'optional':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
            Optional
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-red-50 text-[#F08080]">
            <XCircle className="w-3.5 h-3.5" />
            Disabled
          </span>
        );
    }
  };

  const getDataSourceBadge = (source) => {
    if (!source) return null;
    if (source.includes(' or Dummy') || source.includes('else Dummy')) {
      return (
        <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">
          {source}
        </span>
      );
    }
    if (source === 'Real API' || source === 'Real Data' || source === 'PIN API') {
      return (
        <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-[#E6F7F3] text-[#1C472B]">
          {source}
        </span>
      );
    }
    if (source === 'Dummy' || source.includes('Dummy')) {
      return (
        <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700">
          {source}
        </span>
      );
    }
    return (
      <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
        {source}
      </span>
    );
  };

  return (
    <LayoutComponent>
      <div data-testid="info-page" className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-extrabold text-[#0F172A] ">
            API Information
          </h1>
          <p className="text-slate-500 mt-1">
            Overview of PayItNow Banking API endpoints and data sources
          </p>
        </div>

        {/* API Documentation Link */}
        <Card className="bg-[#1C472B] text-white rounded-2xl border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold  mb-2">PayItNow Banking API</h3>
                <p className="text-slate-300 text-sm">
                  Base URL: <code className="bg-white/10 px-2 py-0.5 rounded">https://sandboxau.banking.payitnow.io/api</code>
                </p>
              </div>
              <a 
                href="https://sandboxau.banking.payitnow.io/docs" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white text-[#1C472B] px-5 py-2.5 rounded-full font-semibold hover:bg-slate-100 hover:text-[#073B2E] transition-colors"
              >
                View API Docs
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </CardContent>
        </Card>

        {/* PayItNow API Endpoints */}
        <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <CardHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
            <CardTitle className="text-xl font-bold text-[#0F172A] ">
              PayItNow API Endpoints
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">External banking API calls used by this application</p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Feature</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">API Endpoint</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Description</th>
                    <th className="text-center px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Data Source</th>
                    <th className="text-center px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {apiEndpoints.map((api, index) => (
                    <tr key={index} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <span className="font-semibold text-[#0F172A]">{api.feature}</span>
                      </td>
                      <td className="px-6 py-4">
                        <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded text-[#1C472B]">
                          {api.endpoint}
                        </code>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-slate-600">{api.description}</p>
                        <p className="text-xs text-slate-400 mt-1">{api.notes}</p>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {getDataSourceBadge(api.dataSource)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {getStatusBadge(api.status)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Application Features */}
        <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <CardHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
            <CardTitle className="text-xl font-bold text-[#0F172A] ">
              Application Features
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">All features and their data sources</p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Feature</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">API / Source</th>
                    <th className="text-center px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Data Source</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {appFeatures.map((feature, index) => (
                    <tr key={index} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <span className="font-semibold text-[#0F172A]">{feature.feature}</span>
                      </td>
                      <td className="px-6 py-4">
                        <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded text-slate-600">
                          {feature.api}
                        </code>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {getDataSourceBadge(feature.dataSource)}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-slate-600">{feature.description}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Legend */}
        <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
          <CardContent className="p-6">
            <h3 className="font-bold text-[#0F172A] mb-4 ">Data Source Legend</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-4 bg-[#E6F7F3] rounded-xl">
                <CheckCircle className="w-5 h-5 text-[#1C472B] mt-0.5" />
                <div>
                  <p className="font-semibold text-[#1C472B]">PIN API / Real Data</p>
                  <p className="text-sm text-[#1C472B]/70">
                    From PayItNow Banking API or our database. Live when credentials are valid.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-700">Dummy / Demo Data</p>
                  <p className="text-sm text-amber-700/70">
                    Simulated when PIN API is unavailable (e.g. no credentials, auth fails). Same structure as real data.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </LayoutComponent>
  );
};

export default Info;
